import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useMediaDevices,
  useMicrophoneDeviceId,
  useScribeStore,
  useTeardown,
} from "../stores/scribe";

export type MicrophoneTestStatus =
  | "idle"
  | "requesting"
  | "listening"
  | "ready"
  | "error"
  | "unsupported";

export type MicrophoneDevice = {
  deviceId: string;
  label: string;
};

const LEVEL_COUNT = 10;
const READY_THRESHOLD = 0.03;

function getAudioContextCtor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  );
}

function toMicrophoneDevices(
  devices: MediaDeviceInfo[] | null,
): MicrophoneDevice[] {
  return (devices ?? []).map((device, index) => ({
    deviceId: device.deviceId,
    label: device.label || `Microphone ${index + 1}`,
  }));
}

export function useMicrophoneLevelTest({
  onReadyChange,
}: {
  onReadyChange?: (ready: boolean) => void;
} = {}) {
  const [status, setStatus] = useState<MicrophoneTestStatus>("idle");
  const [levels, setLevels] = useState<number[]>(() =>
    Array.from({ length: LEVEL_COUNT }, () => 0),
  );
  const [error, setError] = useState<string | null>(null);
  const [mediaDevices, getMediaDevices] = useMediaDevices();
  const [microphoneDeviceId, setMicrophoneDeviceId] = useMicrophoneDeviceId();
  const setupMicrophoneTest = useScribeStore(
    (state) => state.setupMicrophoneTest,
  );
  const teardown = useTeardown();
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const readyRef = useRef(false);

  const devices = useMemo(
    () => toMicrophoneDevices(mediaDevices),
    [mediaDevices],
  );

  const stop = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    void audioContextRef.current?.close();
    audioContextRef.current = null;
    teardown();
  }, [teardown]);

  const analyzeStream = useCallback(
    (stream: MediaStream) => {
      const AudioContextCtor = getAudioContextCtor();
      if (!AudioContextCtor) return;

      const audioContext = new AudioContextCtor();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = audioContext;

      const samples = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteTimeDomainData(samples);
        const peak = samples.reduce((max, value) => {
          return Math.max(max, Math.abs(value - 128) / 128);
        }, 0);

        setLevels((previous) => [...previous.slice(1), peak]);

        if (!readyRef.current && peak >= READY_THRESHOLD) {
          readyRef.current = true;
          setStatus("ready");
          onReadyChange?.(true);
        }

        animationFrameRef.current = window.requestAnimationFrame(tick);
      };

      tick();
    },
    [onReadyChange],
  );

  const start = useCallback(
    async (deviceId = microphoneDeviceId ?? undefined) => {
      if (!navigator.mediaDevices?.getUserMedia || !getAudioContextCtor()) {
        setStatus("unsupported");
        setError("This browser does not support microphone setup.");
        onReadyChange?.(false);
        return;
      }

      stop();
      readyRef.current = false;
      onReadyChange?.(false);
      setError(null);
      setStatus("requesting");
      setLevels(Array.from({ length: LEVEL_COUNT }, () => 0));

      try {
        const nextDevices = await getMediaDevices();
        const currentDeviceId =
          deviceId && nextDevices.some((device) => device.deviceId === deviceId)
            ? deviceId
            : (nextDevices[0]?.deviceId ?? null);

        setMicrophoneDeviceId(currentDeviceId);

        const stream = await setupMicrophoneTest(currentDeviceId ?? undefined);

        if (!stream) {
          setStatus("error");
          setError(
            "We couldn't hear this microphone. Check browser permissions or try another device.",
          );
          onReadyChange?.(false);
          return;
        }

        setStatus("listening");
        analyzeStream(stream);
      } catch {
        setStatus("error");
        setError(
          "We couldn't hear this microphone. Check browser permissions or try another device.",
        );
        onReadyChange?.(false);
      }
    },
    [
      analyzeStream,
      getMediaDevices,
      microphoneDeviceId,
      onReadyChange,
      setMicrophoneDeviceId,
      setupMicrophoneTest,
      stop,
    ],
  );

  const selectDevice = useCallback(
    (deviceId: string) => {
      setMicrophoneDeviceId(deviceId);
      readyRef.current = false;
      onReadyChange?.(false);

      if (status === "listening" || status === "ready") {
        void start(deviceId);
      }
    },
    [onReadyChange, setMicrophoneDeviceId, start, status],
  );

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    devices,
    error,
    levels,
    selectedDeviceId: microphoneDeviceId,
    selectDevice,
    start,
    status,
  };
}
